import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useProviderCredentials } from "@/hooks/useProviderCredentials";
import { useAuth } from "@/contexts/AuthContext";
import { ChatBar } from "@/components/ui";
import { ChatPanel } from "@/components/ui/chat-panel";

const AuditLogsTab = lazy(() =>
  import("@/components/CloudDashboard/audit-logs-tab").then((mod) => ({
    default: mod.AuditLogsTab,
  }))
);
const CostAnalysisTab = lazy(() =>
  import("@/components/CloudDashboard/cost-analysis-tab").then((mod) => ({
    default: mod.CostAnalysisTab,
  }))
);
const ResourcesDashboard = lazy(() =>
  import("@/components/CloudDashboard/resources-dashboard").then((mod) => ({
    default: mod.ResourcesDashboard,
  }))
);

export default function CloudDashboard() {
  const [projectId, setProjectId] = useState<string>("all");
  const { isAuthenticated, isLoading: authLoading, token } = useAuth();

  // Fetch provider credentials to derive projects
  const {
    data: credentials,
    isLoading: loadingProjects,
    isError: error,
  } = useProviderCredentials();

  // Pull BigQuery dataset and table from GCP provider config
  const gcpCredential = credentials?.find((cred) => cred.provider === "gcp");
  const initialDatasetId = gcpCredential?.provider_config?.dataset_id ?? "";
  const initialTableId = gcpCredential?.provider_config?.table_id ?? "";

  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [initialChatMessage, setInitialChatMessage] = useState("");
  const [initialChatFiles, setInitialChatFiles] = useState<File[] | undefined>(
    undefined
  );

  const projects = credentials
    ? credentials.map((cred) => ({
        projectId: String(cred.id ?? cred.account_identifier),
        name: cred.display_name || cred.account_identifier,
      }))
    : [];

  const handleChatMessageSubmit = (message: string, files?: File[]) => {
    setInitialChatMessage(message);
    setInitialChatFiles(files);
    setChatPanelOpen(true);
    // files are now forwarded to ChatPanel via initialChatFiles
  };

  return (
    <div className="p-6 relative min-h-screen pb-24">
      {/* Projects Section */}
      <div className="flex items-center gap-4 mt-4">
        <span className="font-medium">Projects:</span>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-[300px] rounded-[20px]">
            <SelectValue
              placeholder={
                loadingProjects ? "Loading projects..." : "Select a project"
              }
            />
          </SelectTrigger>
          <SelectContent className="rounded-[20px]">
            <SelectItem value="all">All Projects</SelectItem>
            {loadingProjects ? (
              <SelectItem value="loading" disabled>
                Loading...
              </SelectItem>
            ) : error ? (
              <SelectItem value="error" disabled>
                Error loading projects
              </SelectItem>
            ) : projects.length === 0 ? (
              <SelectItem value="no-projects" disabled>
                No projects configured
              </SelectItem>
            ) : (
              projects.map((project) => (
                <SelectItem key={project.projectId} value={project.projectId}>
                  {project.name} ({project.projectId})
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Top Row - Main Metrics (Always Visible)
      <div className="mt-6">
        <TopMetricsCards projectId={projectId} />
      </div> */}

      {/* Tabs Section */}
      <div className="mt-6">
        <Tabs defaultValue="resources" className="w-full">
          <TabsList className="grid w-full grid-cols-5 rounded-[20px]">
            <TabsTrigger value="resources" className="rounded-[16px]">
              Cloud Overview
            </TabsTrigger>
            <TabsTrigger value="audit-logs" className="rounded-[16px]">
              Audit Logs
            </TabsTrigger>
            <TabsTrigger value="cost-analysis" className="rounded-[16px]">
              Cost Analysis
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-[16px]">
              Security
            </TabsTrigger>
            <TabsTrigger value="optimization" className="rounded-[16px]">
              Optimization
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="resources"
            className="space-y6 text-center py-12 text-muted-foreground"
          >
            {loadingProjects || authLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4" />
                <p>Loading resources...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                Error loading data. Please check your credentials.
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="text-center py-12">Loading resources...</div>
                }
              >
                <ResourcesDashboard projectId={projectId} />
              </Suspense>
            )}
          </TabsContent>

          <TabsContent value="audit-logs" className="space-y-6">
            {loadingProjects ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p>Loading audit logs...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                Error loading data. Please check your credentials.
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="text-center py-12">Loading audit logs...</div>
                }
              >
                <AuditLogsTab projectId={projectId} />
              </Suspense>
            )}
          </TabsContent>

          <TabsContent value="cost-analysis" className="space-y-6">
            {loadingProjects ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p>Loading cost analysis data...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                Error loading data. Please check your credentials.
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="text-center py-12">
                    Loading cost analysis...
                  </div>
                }
              >
                <CostAnalysisTab projectId={projectId} />
              </Suspense>
            )}
          </TabsContent>

          <TabsContent
            value="security"
            className="space-y6 text-center py-12 text-muted-foreground"
          >
            Security dashboard coming soon...
          </TabsContent>

          <TabsContent
            value="optimization"
            className="space-y6 text-center py-12 text-muted-foreground"
          >
            Optimization dashboard coming soon...
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating ChatBar (feature-flag gated inside component) */}
      <ChatBar
        onMessageSubmit={handleChatMessageSubmit}
        isVisible={!chatPanelOpen}
      />

      {/* Chat Panel */}
      <ChatPanel
        isOpen={chatPanelOpen}
        onClose={() => setChatPanelOpen(false)}
        initialMessage={initialChatMessage}
        initialFiles={initialChatFiles}
        projectId={projectId === "all" ? null : parseInt(projectId)}
      />
    </div>
  );
}
